// Vacancies and offers waiting on their named approver.
//
// These lived inside OvertimePage, which is how that screen came to hold two
// things it has nothing to do with: a vacancy is not an allowance, and the
// only thing they had in common was that somebody had to decide them. They
// are the Hiring tab of the Approvals page now (21 September 2026), beside
// leave and overtime, which is what the page is for.
//
// Approving a vacancy or an above-band offer is a grant (hiring:approve-vacancy,
// hiring:approve-offer) set in Admin → Access, not a reporting-line question
// like the other two.
import { useState } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
// ── Vacancies awaiting approval ──────────────────────────────────────────────
// Fed by GET /api/talent/approvals. Approve is one click; reject asks for the
// reason inline, next to the vacancy, because that is where the person is.
export function VacancyApprovals({ vacancies, onDecided }) {
  const { toast } = useToast();
  const [rejecting, setRejecting] = useState(null);   // vacancy id with the reason box open
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);

  const decide = async (v, decision) => {
    setBusy(v.id);
    try {
      const res = await api.post(`talent/vacancies/${v.id}/decide`, { decision, note: decision === 'reject' ? reason : undefined });
      // Naming who was emailed, at the button that was pressed, so the approver
      // knows the raiser has been told and does not go and tell them again.
      const notified = Array.isArray(res?.notified) ? res.notified : [];
      const told = notified.length ? ` ${notified.join(' and ')} notified.` : '';
      const outcome = v.reopen_from_status
        ? `Reopening ${v.title} ${decision === 'approve' ? 'approved' : 'turned down'}`
        : `${v.title} ${decision === 'approve' ? 'approved' : 'rejected'}`;
      toast(`${outcome}.${told}`, 'success');
      setRejecting(null); setReason('');
      onDecided?.();
    } catch (e) {
      toast(e.message || 'Could not record the decision', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!vacancies.length) {
    return <div className="bg-white border border-gray-200 rounded p-4 text-xs text-gray-500">No vacancies are waiting for your approval.</div>;
  }
  return (
    <div className="space-y-2">
      {vacancies.map(v => (
        <div key={v.id} className="bg-white border border-gray-200 rounded p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-sm font-bold text-navy">{v.title} <span className="text-[10px] font-semibold text-gray-400">{v.reference}</span></div>
              <div className="text-[10px] text-gray-500">
                {v.team_name || 'No team'} · Hiring manager {v.hiring_manager_name || 'not set'} · Headcount {v.headcount}
                {v.reason ? ` · ${v.reason}` : ''}{v.start_by ? ` · Fill by ${formatDate(v.start_by)}` : ''}
              </div>
              {(v.salary_band_low != null || v.salary_band_high != null) && (
                <div className="text-[10px] text-gray-500">Band £{Number(v.salary_band_low || 0).toLocaleString()} to £{Number(v.salary_band_high || 0).toLocaleString()}</div>
              )}
              <div className="text-[10px] text-gray-400 mt-0.5">Raised by {v.created_by_name || 'unknown'} on {formatDate(v.created_at)}</div>
              {v.reopen_from_status && (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                  Request to reopen this {v.reopen_from_status} vacancy and take the headcount from {v.headcount} to <b>{v.reopen_headcount}</b>
                  {' '}({v.filled_count || 0} filled already). Asked by {v.reopen_requested_by_name || 'unknown'}: {v.reopen_request_reason}
                </div>
              )}
              {v.description && <div className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap">{v.description}</div>}
            </div>
            <div className="flex gap-1">
              <button disabled={busy === v.id} onClick={() => decide(v, 'approve')}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">Approve</button>
              <button disabled={busy === v.id} onClick={() => { setRejecting(rejecting === v.id ? null : v.id); setReason(''); }}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-white border border-gray-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Reject</button>
            </div>
          </div>
          {rejecting === v.id && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder={v.reopen_from_status ? `Reason for turning it down (required); it stays ${v.reopen_from_status}` : 'Reason for rejecting (required)'}
                className="flex-1 min-w-[200px] border border-gray-200 rounded px-2 py-1 text-xs" />
              <button disabled={!reason.trim() || busy === v.id} onClick={() => decide(v, 'reject')}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Confirm rejection</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Offers above the salary band awaiting approval ───────────────────────────
// Fed by GET /api/talent/offer-approvals. The approver sees the figure and the
// band it exceeds, because that is the decision being asked of them.
export function OfferApprovals({ offers, onDecided }) {
  const { toast } = useToast();
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const money = (v) => (v == null ? '—' : `£${Number(v).toLocaleString('en-GB')}`);

  const decide = async (o, decision) => {
    setBusy(o.id);
    try {
      await api.post(`talent/offers/${o.id}/decide`, { decision, note: decision === 'reject' ? reason : undefined });
      toast(decision === 'approve' ? `Offer to ${o.candidate_name} approved` : `Offer to ${o.candidate_name} refused`, 'success');
      setRejecting(null); setReason('');
      onDecided?.();
    } catch (e) {
      toast(e.message || 'Could not record the decision', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!offers.length) {
    return <div className="bg-white border border-gray-200 rounded p-4 text-xs text-gray-500">No offers are waiting for your approval.</div>;
  }
  return (
    <div className="space-y-2">
      {offers.map(o => (
        <div key={o.id} className="bg-white border border-gray-200 rounded p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-sm font-bold text-navy">{o.candidate_name} <span className="text-[10px] font-semibold text-gray-400">{o.vacancy_reference} · {o.vacancy_title}</span></div>
              <div className="text-[11px] text-gray-700 mt-0.5">
                {o.pay_basis === 'contractor' ? (<>
                  Contractor offer <b>{money(o.day_rate)} a day</b> ({o.ir35 === 'inside' ? 'inside' : 'outside'} IR35), above the approved day rate of {money(o.day_rate_band_low)} to {money(o.day_rate_band_high)}
                  {o.day_rate_band_high != null && <span className="text-amber-700"> (+{money(o.day_rate - o.day_rate_band_high)})</span>}
                </>) : (<>
                  {o.contract_type === 'fixed_term' ? 'Fixed-term offer' : 'Offer'} <b>{money(o.salary)}</b>, above the approved band of {money(o.salary_band_low)} to {money(o.salary_band_high)}
                  {o.salary_band_high != null && <span className="text-amber-700"> (+{money(o.salary - o.salary_band_high)})</span>}
                </>)}
              </div>
              <div className="text-[10px] text-gray-500">
                Start {formatDate(o.start_date)}{o.contract_type === 'fixed_term' ? `, ending ${formatDate(o.end_date)}` : ''} · {o.clearance_target === 'bpss' ? 'BPSS' : 'SC'} clearance
                {o.conditions ? ` · ${o.conditions}` : ''}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">Made by {o.created_by_name || 'unknown'} on {formatDate(o.created_at)}</div>
              {o.notes && <div className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap">{o.notes}</div>}
            </div>
            <div className="flex gap-1">
              <button disabled={busy === o.id} onClick={() => decide(o, 'approve')}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">Approve</button>
              <button disabled={busy === o.id} onClick={() => { setRejecting(rejecting === o.id ? null : o.id); setReason(''); }}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-white border border-gray-200 text-red-700 hover:bg-red-50 disabled:opacity-50">Refuse</button>
            </div>
          </div>
          {rejecting === o.id && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for refusing (required), e.g. hold to the band"
                className="flex-1 min-w-[200px] border border-gray-200 rounded px-2 py-1 text-xs" />
              <button disabled={!reason.trim() || busy === o.id} onClick={() => decide(o, 'reject')}
                className="px-3 py-1 text-[10px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Confirm refusal</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
